import {
  Center,
  Container,
  Heading,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Enrolments } from './Enrolments';
import { FlowEditorStateProvider } from './components/ReactFlow/FlowEditorStateContext';
import { PageEditorStateProvider } from './components/ReactFlow/PageEditorStateContext';
import { NotFound } from '@duke-hq/ui/components/NotFound/NotFound';
import FiltersProvider from '@duke-hq/filters/context/FiltersProvider';
import { Flag, useFlag } from '@duke-hq/flags/react';
import { Route, Routes, Outlet, Navigate } from 'react-router-dom';
import { LoginPage } from './LoginPage';
import { MagicLinkPage } from './MagicLinkPage';
import { SignOut } from './SignOut';
import { useTenantContext } from './TenantContext';
import { GifModal } from './components/ReactFlow/nodes/blocknode/GifModal';
import { Analytics } from './Analytics';
import { AssessmentsPage, AssessmentsLayout, AssessmentDashboard } from './Assessments';
import {
  CourseEditor,
  CourseEditorProvider,
  CoursesPageAdmin,
  CourseTranslationCoverage,
} from './Courses';
import { Dashboard } from './Dashboard';
import { EditResource } from './EditResource';
import { LayoutAdmin } from './LayoutAdmin';
import {
  LocationsPage,
  ProviderCourses,
  ProviderDetails,
  ProviderSettings,
  ProvidersPage,
  ProviderStudents,
  ProviderConsultants,
  ProvidersLayout,
  ProviderReports,
  BillingRulesPage,
} from './Providers';
import { StudentsPage } from './Students';
import { SupportTicketsPage } from './SupportTickets/Page';
import { SupportTasksPage } from './SupportTasks/SupportTasksPage';
import { UnitAssessmentPage } from './UnitAssessment';
import {
  ConversationalGuidePage,
  UnitsOfCompetency,
  UnitsOfCompetencyEditPage,
  UnitsLayout,
} from './UnitsOfCompetency';
import { AdminUsersPage } from './Users';
import CourseEnrolmentEventLogs, {
  FinancesLayout,
  InvoicesPage,
  ProviderCausesLayout,
} from './Finances';
import { AvetmissPage } from './Avetmiss/AvetmissLayout';
import { IdentityPage } from './Organisation/IdentityPage';
import { TemplatesListPage } from './Templates/TemplatesListPage';
import { TemplateDetailPage } from './Templates/TemplateDetailPage';
import {
  PlacementsLayout,
  Students as PlacementsStudents,
  Employers as PlacementsEmployers,
  Courses as PlacementsCourses,
} from './WorkPlacements';
import { DataMigrationsPage } from './DataMigrations';
import { SeedsPage } from './Developer/Seeds/SeedsPage';
import { TranslationQueuePage } from './Developer/TranslationQueue';
import { ScheduledTasksPage } from './ScheduledTasks';
import { AutomationRulesPage } from './AutomationRules';
import { SupportTasksLayout } from './SupportTasks/SupportTasksLayout';
import { InvoicesXeroPage } from './Finances/InvoicesXero';
import {
  AiPlaygroundPage,
  DevelopersPage,
  FeatureFlagsPage,
  FilesLayout,
  FilesUploadPage,
  FilesListPage,
  DbStatsLayout,
  DbStatsPanelPage,
  DbStatsIndex,
} from './Developer';
import { BillableItems } from './Finances/Billing/BillingItemsPage';
import { ContentRolesPage } from './ContentRoles/ContentRolesPage';
import { ContentPersonasPage } from './ContentPersonas/ContentPersonasPage';
import { ContentProfilesPage } from './ContentProfiles/ContentProfilesPage';
import { UnitGenerationPage } from './UnitsOfCompetency/UnitGeneration/UnitGenerationPage';
import { UnitTranslationCoverage } from './UnitsOfCompetency/UnitTranslationCoverage';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { HomePage } from './HomePage';

export function AdminRoutes(): React.JSX.Element {
  const tenantCtx = useTenantContext();

  return (
    <FlowEditorStateProvider>
      <PageEditorStateProvider>
        <CourseEditorProvider>
          <GifModal />
          <Routes>
            <Route element={<LayoutAdmin />}>
              <Route
                path="/demo"
                element={
                  <Flag.Boolean
                    flagKey="DEMO_FEATURE"
                    whenTrue={DemoPage()}
                    whenFalse={<NotFound />}
                  />
                }
              />
              <Route path="/scheduled-tasks" element={<ScheduledTasksPage />} />
              <Route path="/scheduled-tasks/:selectedTaskName" element={<ScheduledTasksPage />} />
              <Route path="/courses" element={<CoursesPageAdmin />} />
              <Route path="/courses/:courseId" element={<CourseEditor />} />
              <Route
                path="/courses/:courseId/translations"
                element={<CourseTranslationCoverage />}
              />

              <Route path="/units" element={<UnitsLayout />}>
                <Route index element={<UnitsOfCompetency />} />
                <Route path=":unitId" element={<UnitsOfCompetencyEditPage />} />
                <Route path=":unitId/assessment" element={<UnitAssessmentPage />} />
                <Route path=":unitId/translations" element={<UnitTranslationCoverage />} />
                <Route path=":unitId/conversational-guide" element={<ConversationalGuidePage />} />
                <Route path="content-roles" element={<ContentRolesPage />} />
                <Route path="content-personas" element={<ContentPersonasPage />} />
                <Route path="content-profiles" element={<ContentProfilesPage />} />
                <Route path="generation" element={<UnitGenerationPage />} />
              </Route>
              <Route path="/support/:ticketId" element={<SupportTicketsPage />} />
              <Route path="/support" element={<SupportTicketsPage />} />

              <Route element={<SupportTasksLayout />}>
                <Route path="/tasks/automation-rules" element={<AutomationRulesPage />} />
                <Route path="/tasks/automation-rules/:ruleId" element={<AutomationRulesPage />} />
                <Route path="/tasks" element={<SupportTasksPage />} />
                <Route path="/tasks/:taskId" element={<SupportTasksPage />} />
              </Route>

              <Route path="/resources/:resourceId" element={<EditResource />} />
              <Route path="/home" element={<Dashboard />} />

              <Route path="/users" element={<AdminUsersPage />} />
              <Route path="/users/:userId/:tab?" element={<AdminUsersPage />} />

              <Route path="/providers" element={<ProvidersLayout />}>
                <Route index element={<ProvidersPage />} />
                <Route path="reports" element={<ProviderReports />} />
                <Route path=":providerId" element={<ProviderDetails />}>
                  <Route path="settings" element={<ProviderSettings />} />
                  <Route path="courses" element={<ProviderCourses />} />
                  <Route path="students" element={<ProviderStudents />} />
                  <Route path="students/:studentId" element={<ProviderStudents />} />
                  <Route path="consultants" element={<ProviderConsultants />} />
                  <Route path="consultants/:consultantId/:tab?" element={<ProviderConsultants />} />
                  <Route path="locations" element={<LocationsPage />} />
                  <Route path="locations/site/:siteId" element={<LocationsPage />} />
                  <Route path="locations/region/:regionId" element={<LocationsPage />} />
                  <Route path="billing-rules" element={<BillingRulesPage />} />
                </Route>
              </Route>

              <Route path="/analytics" element={<Analytics />} />

              <Route path="/assessments" element={<AssessmentsLayout />}>
                <Route
                  element={
                    <FiltersProvider
                      key="assessments-list"
                      localStorageKey="assessments_list_filters"
                    >
                      <Outlet />
                    </FiltersProvider>
                  }
                >
                  <Route index element={<AssessmentsPage />} />
                  <Route path=":assessmentId" element={<AssessmentsPage />} />
                </Route>
                <Route
                  path="dashboard"
                  element={
                    <FiltersProvider
                      key="assessments-dashboard"
                      localStorageKey="assessments_dashboard_filters"
                    >
                      <AssessmentDashboard />
                    </FiltersProvider>
                  }
                />
              </Route>

              <Route path="/students" element={<StudentsPage />} />
              <Route path="/student/:studentId/:tab?/:value?" element={<StudentsPage />} />

              <Route
                element={
                  <FiltersProvider localStorageKey="enrolments_page_filters">
                    <Outlet />
                  </FiltersProvider>
                }
              >
                <Route path="/enrolments">
                  <Route index element={<Enrolments />} />
                </Route>
                <Route path="/enrolment/:enrolmentId" element={<Enrolments />} />
              </Route>

              <Route path="/templates" element={<TemplatesListPage />} />
              <Route path="/templates/:templateName" element={<TemplateDetailPage />} />
              <Route path="/organisation" element={<IdentityPage />} />

              <Route path="/placements" element={<PlacementsLayout />}>
                <Route index element={<Navigate to="/placements/students" replace />} />
                <Route path="students" element={<PlacementsStudents />} />
                <Route path="students/:enrolmentId" element={<PlacementsStudents />} />
                <Route path="employers" element={<PlacementsEmployers />} />
                <Route path="employers/:employerId" element={<PlacementsEmployers />} />
                <Route path="employers/:employerId/:tab" element={<PlacementsEmployers />} />
                <Route path="courses" element={<PlacementsCourses />} />
              </Route>

              <Route path="/finances" element={<FinancesLayout />}>
                <Route path="/finances/invoices" element={<InvoicesPage />} />
                <Route
                  path="/finances/course-enrolment-events"
                  element={<CourseEnrolmentEventLogs />}
                />
                <Route path="/finances/provider-causes" element={<ProviderCausesLayout />} />
                <Route path="/finances/invoicesv2" element={<InvoicesXeroPage />} />
                <Route path="/finances/billable-items" element={<BillableItems />} />
              </Route>

              <Route path="/avetmiss/*" element={<AvetmissPage />} />

              {/* Developer Tools */}
              <Route path="/developer-tools" element={<DevelopersPage />} />
              <Route path="/developer-tools/ai-playground" element={<AiPlaygroundPage />} />
              <Route path="/developer-tools/feature-flags" element={<FeatureFlagsPage />} />
              <Route path="/developer-tools/translation-queue" element={<TranslationQueuePage />} />
              <Route path="/developer-tools/data-migrations" element={<DataMigrationsPage />} />
              <Route
                path="/developer-tools/data-migrations/:selectedProcedureName"
                element={<DataMigrationsPage />}
              />
              <Route path="/developer-tools/seeds" element={<SeedsPage />} />
              <Route path="/developer-tools/seeds/:selectedStrategyName" element={<SeedsPage />} />

              <Route path="/developer-tools/db-stats" element={<DbStatsLayout />}>
                <Route index element={<DbStatsIndex />} />
                <Route path=":panelId" element={<DbStatsPanelPage />} />
              </Route>

              <Route path="/developer-tools/files" element={<FilesLayout />}>
                <Route index element={<FilesUploadPage />} />
                <Route path="upload" element={<FilesUploadPage />} />
                <Route path="list" element={<FilesListPage />} />
              </Route>

              <Route path="/" element={<HomePage />} />
              <Route path="*" element={<NotFound />}></Route>
            </Route>
            <Route path="/login" element={<LoginPage />} />
            {tenantCtx &&
              (tenantCtx.environment === 'local' || tenantCtx.environment === 'test') && (
                <Route path="/magiclink" element={<MagicLinkPage />} />
              )}
            <Route path="/signout" element={<SignOut />} />
          </Routes>
        </CourseEditorProvider>
      </PageEditorStateProvider>
    </FlowEditorStateProvider>
  );
}

export function DemoPage(): React.JSX.Element {
  // useFlag is a hook that returns the value of a feature flag
  const DEMO_FEATURE = useFlag('DEMO_FEATURE');
  const DEMO_RELEASE = useFlag('DEMO_RELEASE');
  const DEMO_EXPERIMENT = useFlag('DEMO_EXPERIMENT');

  return (
    <Center>
      <Container>
        <Heading>Demo Feature Flag</Heading>
        <Text>This page demonstrates how to use feature flags.</Text>

        <Table size="sm" marginTop={pxToRem(32)}>
          <Thead>
            <Tr>
              <Th>Flag</Th>
              <Th>Enabled</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>{DEMO_FEATURE ? '✅' : '❌'}</Td>
              <Td>
                <pre>DEMO_FEATURE</pre>
              </Td>
            </Tr>
            <Tr>
              <Td>{DEMO_RELEASE ? '✅' : '❌'}</Td>
              <Td>
                <pre>DEMO_RELEASE</pre>
              </Td>
            </Tr>
            <Tr>
              <Td>{DEMO_EXPERIMENT ? '✅' : '❌'}</Td>
              <Td>
                <pre>DEMO_EXPERIMENT</pre>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Container>
    </Center>
  );
}
